/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import PayBox from './PayBox';
import { PaylineEvent } from './PaylineEvent';
import PaylineManager from './PaylineManager';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Implementation of [[PaylineManager]] for representing the payline types used
 * in video style slot games.
 */
@cc._decorator.ccclass
export default class VideoPaylineManager extends PaylineManager
{
    /** [[PayBox]] objects from each reel, should be ordered parallel to the reels they represent. */
    @property({ type: [PayBox], visible: true })
    private _payboxes: PayBox[] = [];

    /** @inheritDoc */
    public ClearPaylines(): void
    {
        this.destroyPayboxes();
        super.ClearPaylines();
    }

    /** @inheritDoc */
    protected cyclePayline(): void
    {
        this.destroyPayboxes();
        super.cyclePayline();
    }

    /**
     * Displays the payboxes over the associated symbols and draws connecting lines.
     * Also performs color gradient cycling if configured.
     */
    // TODO: Add support for multiple payboxes per reel.
    protected showCurrentPayline(): void
    {
        // The reel offsets for this payline (0: center, 1: bottom, -1: top)
        const offsetSet: number[] = this.paylineOffsets[this.currentPaylineIndex];
        const isScatter: boolean = offsetSet.length > this._reelManagerRef.Value.ReelCount;

        this.paylineOffsets
                .filter(offset => offset !== offsetSet)
                .forEach(offset => this.sendPaylineEvent(PaylineEvent.OnClear, offset), this);

        const paylineStrings: string = this.result.paylinePrizeString[this.currentPaylineIndex];

        const maxSymbolAnimDuration: number
            = this.animatePaylineSymbols(PaylineEvent.OnActive, offsetSet, isScatter, paylineStrings);
        this.currentCycleTime = maxSymbolAnimDuration > this._cycleTime ? maxSymbolAnimDuration : this._cycleTime;

        for (const reelIndexKey in offsetSet)
        {
            const reelIndex: number = this.result.paylineReelIndex[this.currentPaylineIndex][reelIndexKey];
            const paylineChar: string = paylineStrings[reelIndexKey];

            if (!paylineChar) { continue; }

            const offset: number = offsetSet[reelIndexKey];

            // Tell the paybox to display itself and send along all the information it needs.
            const paybox: PayBox = this._payboxes[reelIndex];
            const color: cc.Color = this.paylineColorsRef
                .WithValue(paylineColor => paylineColor.GetColor(this.currentPaylineIndex));

            if (!paybox)
            {
                CLog.error("No paybox set for this reel.", this, LogTypes.Reel);
            }
            else if ((isScatter && paylineChar !== "?") || !isScatter)
            {
                paybox.Display(paylineChar !== "?", reelIndex, offset,
                    reelIndex > 0 && !isScatter ? this._payboxes[reelIndex - 1] : null, color);
            }
        }

        this.buildPaylineMessage(this.currentPaylineIndex);

        this.applyDTModeToCycleTime();
        // Schedule the next payline cycle.
        this.scheduleOnce(() => this.cyclePayline(), this.currentCycleTime);
    }

    /** @hidden */
    private destroyPayboxes(): void
    {
        this._payboxes.forEach(paybox => paybox.Hide());
    }
}
